import { COLS, ROWS, COLORS } from './constants.js';

export class Board {
    constructor(ctx) {
        this.ctx = ctx;
        this.grid = this.getEmptyBoard();
        this.piece = null;
        this.score = 0;
        this.lines = 0;
        this.level = 1;
        this.dropTime = 0;
        this.dropInterval = 8000; // Very slow initial speed
        this.dropSpeed = 0.02; // Visual movement speed (pixels per frame)
    }

    reset() {
        this.grid = this.getEmptyBoard();
        this.score = 0;
        this.lines = 0;
        this.level = 1;
        this.dropInterval = 8000; // Very slow initial speed
        this.dropSpeed = 0.02; // Visual movement speed (pixels per frame)
    }

    getEmptyBoard() {
        return Array.from({ length: ROWS }, () => Array(COLS).fill(0));
    }

    valid(p) {
        return p.shape.every((row, dy) => {
            return row.every((value, dx) => {
                const x = p.x + dx;
                const y = p.y + dy;
                return (
                    this.isEmpty(value) ||
                    (this.insideWalls(x) && this.aboveFloor(y) && this.isValidPosition(x, y))
                );
            });
        });
    }

    isEmpty(value) {
        return value === 0;
    }

    insideWalls(x) {
        return x >= 0 && x < COLS;
    }

    aboveFloor(y) {
        return y < ROWS;
    }

    isValidPosition(x, y) {
        return y < 0 || this.grid[y][x] === 0;
    }

    drawBoard() {
        this.grid.forEach((row, y) => {
            row.forEach((value, x) => {
                if (value > 0) {
                    this.ctx.fillStyle = COLORS[value - 1];
                    this.ctx.fillRect(x, y, 1, 1);
                    this.ctx.strokeStyle = '#000';
                    this.ctx.lineWidth = 0.05;
                    this.ctx.strokeRect(x, y, 1, 1);
                }
            });
        });
    }

    merge() {
        const pieceOrder = ['I', 'J', 'L', 'O', 'S', 'T', 'Z'];
        const colorIndex = pieceOrder.indexOf(this.piece.pieceType) + 1;

        this.piece.shape.forEach((row, y) => {
            row.forEach((value, x) => {
                if (value > 0) {
                    this.grid[this.piece.y + y][this.piece.x + x] = colorIndex;
                }
            });
        });
    }

    clearLines() {
        let linesCleared = 0;

        for (let y = this.grid.length - 1; y >= 0; y--) {
            if (this.grid[y].every(value => value > 0)) {
                this.grid.splice(y, 1);
                this.grid.unshift(Array(COLS).fill(0));
                linesCleared++;
                y++; // Re-check the same line
            }
        }

        if (linesCleared > 0) {
            this.lines += linesCleared;
            this.score += this.calculateScore(linesCleared);
            this.level = Math.floor(this.lines / 10) + 1;
            this.dropInterval = Math.max(1500, 8000 - (this.level - 1) * 300);

            // Debug logging
            console.log(`Lines cleared: ${linesCleared}, Total lines: ${this.lines}, Level: ${this.level}, Score: ${this.score}`);
        }
    }

    calculateScore(linesCleared) {
        const points = [0, 100, 300, 500, 800];
        return points[linesCleared] * this.level;
    }

    drop() {
        this.piece.y++;
        if (!this.valid(this.piece)) {
            this.piece.y--;
            this.merge();
            this.clearLines();
            return false;
        }
        return true;
    }

    movePiece(dx, dy) {
        const newPosition = this.piece.getNextPosition(dx, dy);
        if (this.valid(newPosition)) {
            this.piece.move(newPosition);
            this.piece.yFloat = 0; // Reset smooth movement
            return true;
        }
        return false;
    }

    rotatePiece() {
        const rotatedPosition = this.piece.getRotatedPosition();
        if (this.valid(rotatedPosition)) {
            this.piece.move(rotatedPosition);
            this.piece.yFloat = 0; // Reset smooth movement
            return true;
        }
        return false;
    }

    hardDrop() {
        while (this.drop()) {
            this.score += 2;
        }
        // After hard drop, the piece should be null and ready for next piece
    }

    update(deltaTime) {
        this.dropTime += deltaTime;

        // Smooth visual movement
        if (this.piece) {
            this.piece.yFloat += this.dropSpeed;
            if (this.piece.yFloat >= 1) {
                this.piece.yFloat = 0;
                this.piece.y++;
                if (!this.valid(this.piece)) {
                    this.piece.y--;
                    this.merge();
                    this.clearLines();
                    return false; // Signal that piece landed
                }
            }
        }

        // Original drop logic for hard drops and manual drops
        if (this.dropTime > this.dropInterval) {
            if (this.piece && this.piece.yFloat === 0) {
                if (!this.drop()) {
                    return false; // Signal that piece landed
                }
            }
            this.dropTime = 0;
        }

        return true; // Signal that piece is still falling
    }

    draw() {
        this.drawBoard();
    }
}